import orderBy from 'lodash/orderBy';

import {LabelTheme, Section, SectionItem} from 'common/components/items-menu/types';
import {getIconForEvent, renameEvent} from 'common/report/util/events';
import {baseComparator} from 'common/util';
import {hasUserFilters, isAutoTrackCustomEvent} from 'common/util/custom_events';
import {EventDefinition, MpEvent} from 'common/util/definitions-types';
import {featuresStore} from 'common/report/stores/features';
import {
  Permission,
  ProjectFeatureFlag,
  SESSION_END_EVENT_NAME,
  SESSION_START_EVENT_NAME,
  SPECIAL_EVENTS,
} from 'common/report/constants';
import {customEventsStore} from 'common/report/stores/custom-events';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';
import {topEventsStore} from 'common/report/stores/events';
import {currentProjectStore} from 'common/report/stores/project-details';
import {Store} from 'common/report/stores/store';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {lexicalCompose} from 'common/util/function';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {getTooltipDataForEvent} from 'common/widgets/context-panel/util';
import {assertUnreachable} from 'common/util/typescript';

import {ActionType, EventItem, EventMenuItem, StageableMenuItem, StageTagMenuItem} from '../events-menu/types';
import {
  ANY_EVENT,
  CREATE_CUSTOM_EVENT,
  SESSION_END_EVENT,
  SESSION_START_EVENT,
  TAGS_MENU,
} from '../events-menu/constants';
import {MenuType} from '../events-menu/types';
import {getEventMenuItemSublabel, sortItemsByLabel} from '../util';
import {SortBy, SortConfig} from '../properties-menu/types';
import {BaseDataController} from './base-controller';

const orderByLabel = lexicalCompose(baseComparator({transform: (item) => item.label && item.label.toLowerCase()}));

export interface EventsControllerSectionConfig {
  dataGroupId?: string;
  excludeAutotrackEvents?: boolean;
  excludeCustomEvents?: boolean;
  hideEditControls?: boolean;
  includeTags?: boolean;
  selected?: EventItem;
  showAllEvents?: boolean;
  showAnyEvent?: boolean;
  includeHiddenSections?: boolean;
  showSessionEvent?: boolean;
  showSpecialDatasetProperty?: boolean;
  showTopEvents?: boolean;
  mainSectionShowLimit?: number;
  stagedItem?: StageableMenuItem;
  sortConfig?: SortConfig;
  recentlyUsedEvents?: EventItem[];
}

export class EventsController extends BaseDataController {
  protected onStoreCallback = () => this.updateEventItems();
  public data = new Map<string, SectionItem<EventItem>>();
  public loading: boolean;

  get providerStores(): Array<Store<unknown>> {
    return [customEventsStore, topEventsStore, eventDefinitionsStore];
  }

  get eventsStoreSource() {
    return this.showDataDefinitionsDropdowns() ? eventDefinitionsStore : topEventsStore;
  }

  public isNewMenu(): boolean {
    return featuresStore.value?.hasProjectFeatureFlag(ProjectFeatureFlag.NewDropdowns);
  }

  public canWriteCustomEvents(): boolean {
    return currentProjectStore.value?.hasPermission(Permission.WriteCustomEvents);
  }

  public fetchData() {
    if (this.loading) {
      return;
    }

    this.loading = true;
    this.data = new Map();

    customEventsStore.fetch({projectId: getProjectId(), workspaceId: getWorkspaceId()});

    // dropdowns fetch from data definitions endpoints unless explicitly opted out from the feature flag
    this.eventsStoreSource.fetch({
      projectId: getProjectId(),
      workspaceId: getWorkspaceId(),
    });

    this.updateComponents();
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public getSections({
    dataGroupId = null,
    excludeAutotrackEvents,
    excludeCustomEvents,
    hideEditControls,
    includeTags = true,
    selected,
    showAllEvents,
    showAnyEvent,
    includeHiddenSections = true,
    showSessionEvent,
    showSpecialDatasetProperty,
    showTopEvents,
    mainSectionShowLimit = 0,
    stagedItem,
    sortConfig = null,
    recentlyUsedEvents = [],
  }: EventsControllerSectionConfig = {}): Array<Section<EventMenuItem>> {
    let eventItems = this.filterEventItems({
      dataGroupId,
      showSessionEvent: !dataGroupId && !showSpecialDatasetProperty && showSessionEvent,
      excludeAutotrackEvents,
      excludeCustomEvents,
    });

    const hasSelectedValue = selected?.value ?? false;
    if (hasSelectedValue) {
      eventItems = eventItems.map((event) => ({
        ...event,
        isSelected: event.value.value === selected.value,
      }));
    }

    if (stagedItem) {
      switch (stagedItem.menuType) {
        case MenuType.Tags:
          // return the list of tags in the project
          return [this.getEventTagsSection(excludeCustomEvents, this.isNewMenu() ? null : 2)];
        case MenuType.Tag: {
          return [
            {
              isLoading: this.loading,
              label: `Events tagged as ${stagedItem.value}`,
              items: eventItems.filter((item) =>
                eventDefinitionsStore.value[item.value.value]?.tags.includes(stagedItem.value),
              ),
            },
          ];
        }
        default:
          assertUnreachable(stagedItem);
      }
    }

    const additionalItems: Array<SectionItem<EventMenuItem>> = [];

    // Tags item
    if (includeTags && this.isNewMenu()) {
      const tagsSection = this.getEventTagsSection(excludeCustomEvents, null);
      if (tagsSection.items.length > 0) {
        additionalItems.push(TAGS_MENU);
      }
    }

    // Create Custom Event
    const canWriteCustomEvents = !excludeCustomEvents && !hideEditControls && this.canWriteCustomEvents();
    if (canWriteCustomEvents && !this.isNewMenu()) {
      additionalItems.push(CREATE_CUSTOM_EVENT);
    }

    // Any Event
    if (showAnyEvent) {
      additionalItems.push({
        ...ANY_EVENT,
        labelTheme: this.isNewMenu() ? LabelTheme.Italic : null,
        isSelected: hasSelectedValue,
      });
    }

    // All Events
    if (showAllEvents) {
      additionalItems.push({
        icon: this.isNewMenu() ? null : `star-top-events`,
        label: renameEvent(SPECIAL_EVENTS.ALL_EVENTS),
        labelTheme: this.isNewMenu() ? LabelTheme.Italic : null,
        value: {
          value: SPECIAL_EVENTS.ALL_EVENTS,
          label: renameEvent(SPECIAL_EVENTS.ALL_EVENTS),
        },
      });
    }

    // appends Session Events when events do not come from data definitions
    const shouldShowSessionEvents = !dataGroupId && !showSpecialDatasetProperty && showSessionEvent;
    if (!this.showDataDefinitionsDropdowns() && shouldShowSessionEvents) {
      additionalItems.push(SESSION_START_EVENT, SESSION_END_EVENT);
    }

    // Your Top Event
    if (showTopEvents && !showSpecialDatasetProperty) {
      /** @todo [jakub] address top events issue, only allow top events if not a rollup project */
      additionalItems.push({
        label: renameEvent(SPECIAL_EVENTS.TOP_EVENTS),
        labelTheme: this.isNewMenu() ? LabelTheme.Italic : null,
        value: {
          value: SPECIAL_EVENTS.TOP_EVENTS,
          label: renameEvent(SPECIAL_EVENTS.TOP_EVENTS),
        },
        icon: this.isNewMenu() ? null : `star-top-events`,
      });
    }

    const sections: Array<Section<EventMenuItem>> = [];

    if (includeTags && !this.isNewMenu()) {
      sections.push(this.getEventTagsSection(excludeCustomEvents));
    }

    sections.push({
      label: this.isNewMenu() ? null : `Events`,
      isLoading: this.loading,
      items: this.sortItems(eventItems, additionalItems, sortConfig, recentlyUsedEvents),
      showLimit: mainSectionShowLimit,
    });

    if (includeHiddenSections) {
      sections.push(this.getHiddenEventSection());
    }

    return sections;
  }

  sortItems(
    eventItems: Array<SectionItem<EventItem>>,
    additionalItems: Array<SectionItem<EventMenuItem>>,
    sortConfig: SortConfig,
    recentlyUsedEvents: EventItem[],
  ): Array<SectionItem<EventMenuItem>> {
    if (!sortConfig) {
      return [...additionalItems, ...eventItems];
    }
    switch (sortConfig.by) {
      case SortBy.Alphabetical:
        return [...additionalItems, ...sortItemsByLabel(eventItems, {order: sortConfig.order})];
      case SortBy.Suggested:
        return [...additionalItems, ...this.sortEventsByScore(eventItems, recentlyUsedEvents)];
      default:
        assertUnreachable(sortConfig);
    }
  }

  getHiddenEventSection(): Section<EventItem> {
    const eventDefinitions = Object.values(eventDefinitionsStore.value);
    const items = eventDefinitions
      .filter((def) => def.hidden)
      .map((def) => getDataDefinitionsEventMenuItem(def, this.isNewMenu()));
    return {
      items,
      label: `Hidden Events`,
      isHidden: true,
    };
  }

  getEventTagsSection(excludeCustomEvents = false, showLimit = 2): Section<StageTagMenuItem> {
    const tagCounts: {[tagName: string]: number} = {};
    for (const eventKey of this.data.keys()) {
      eventDefinitionsStore.value[eventKey]?.tags
        .filter(() => !(excludeCustomEvents && this.data.get(eventKey)?.value.custom))
        .forEach((tag) => (tagCounts[tag] = tagCounts[tag] ? tagCounts[tag] + 1 : 1));
    }

    const eventStageTagMenuItems: Array<SectionItem<StageTagMenuItem>> = []; // TODO: our sorting functions breaks type inference
    for (const tagName in tagCounts) {
      const count = tagCounts[tagName];
      eventStageTagMenuItems.push({
        label: `${tagName} (${count})`,
        icon: this.isNewMenu() ? null : `tag`,
        hasCaret: true,
        value: {
          action: ActionType.StageMenu,
          menuType: MenuType.Tag,
          value: tagName,
          count,
        },
      });
    }

    return {
      label: `Events tagged as`,
      items: eventStageTagMenuItems.sort(orderByLabel),
      showLimit,
    };
  }

  getCustomEventItems(excludeCustomEvents = false): Array<SectionItem<EventItem>> {
    if (excludeCustomEvents) {
      return [];
    }

    const customEvents = Object.values(customEventsStore.value)
      // entity-sharing : hide custom events that user does not have access to from menus
      .filter((ce) => ce.can_view);

    return customEvents.map((ce) => {
      return getCustomEventMenuItem(ce, eventDefinitionsStore.value[`$custom_event:${ce.id}`], this.isNewMenu());
    });
  }

  getEventsItems(includeHidden = false): Array<SectionItem<EventItem>> {
    let eventItems: Array<SectionItem<EventItem>> = [];

    if (this.showDataDefinitionsDropdowns()) {
      // we filter out custom events here to prevent double count. custom events from data definitions store & custom events store are stitched together in getCustomEventItems
      // eventually, we want to abstract the stitching to the definitions store
      eventItems = Object.values(eventDefinitionsStore.value)
        .filter((ed) => !ed.customEventId && (includeHidden ? true : !ed.hidden))
        .map((item) => getDataDefinitionsEventMenuItem(item, this.isNewMenu()));
    } else {
      eventItems = Object.values(topEventsStore.value).map((event) => {
        return getEventMenuItem(event, eventDefinitionsStore.value[event.name], this.isNewMenu());
      });
    }

    return eventItems;
  }

  updateEventItems() {
    let items = [...this.getEventsItems(), ...this.getCustomEventItems()];
    if (this.showDataDefinitionsSorting()) {
      items = orderBy(items, ({uiUniqueQueryCount}) => uiUniqueQueryCount || 0, `desc`);
      items = items.map(getEventMenuItemSublabel);
    } else {
      items = items.sort(orderByLabel);
    }

    this.loading = false;
    this.data = new Map(items.map((item) => [item.value.value, item]));
    this.updateComponents();
  }

  /** filters out events based on dropdown configuration */
  filterEventItems({
    showSessionEvent = false,
    excludeAutotrackEvents = false,
    excludeCustomEvents = false,
    dataGroupId = null,
  } = {}): Array<SectionItem<EventItem>> {
    const customEvents = Object.values(customEventsStore.value);
    const eventItems = Array.from(this.data.values());

    return eventItems.filter(({value: eventItem}) => {
      if (eventItem.custom) {
        if (excludeCustomEvents) {
          return false;
        }
        const customEvent = customEventsStore.value[eventItem.id];

        // filters out auto track custom events
        if (excludeAutotrackEvents) {
          return !isAutoTrackCustomEvent(customEvent, customEvents);
          // filters out custom events with user filters when looking at a different data group
        } else if (dataGroupId) {
          return !hasUserFilters(customEvent, customEvents);
        }
        return true;
      }

      // filters out session events from event definitions when session events are not supported
      return [SESSION_START_EVENT_NAME, SESSION_END_EVENT_NAME].includes(eventItem.value)
        ? showSessionEvent && !dataGroupId
        : true;
    });
  }

  public showDataDefinitionsDropdowns(): boolean {
    return !featuresStore.value?.hasProjectFeatureFlag(ProjectFeatureFlag.DropdownsQueryApi);
  }

  public showDataDefinitionsSorting(): boolean {
    return (
      this.showDataDefinitionsDropdowns() &&
      featuresStore.value?.hasProjectFeatureFlag(ProjectFeatureFlag.DropdownsSortedUnique)
    );
  }

  public getContextPanelData(
    item: EventItem,
    {allowEdit = false, isLoadingAction = false, sampleEventUrl = ``, canProvideSampleUrl = true} = {},
  ): ContextPanelData {
    const canWriteDataDefinition = currentProjectStore.value?.hasPermission(Permission.WriteDataDefinitions);
    const eventDefinitions = Object.values(eventDefinitionsStore.value);

    if (!item) {
      return null;
    }

    return getTooltipDataForEvent((item as unknown) as MpEvent, eventDefinitions, {
      allowEdit: allowEdit && this.canWriteCustomEvents(),
      allowDescriptionEdit: canWriteDataDefinition,
      isLoadingAction,
      sampleEventUrl,
      canProvideSampleUrl,
    });
  }

  private sortEventsByScore(
    events: Array<SectionItem<EventMenuItem>>,
    recentlyUsedEvents: EventItem[],
  ): Array<SectionItem<EventMenuItem>> {
    const recentlyUsedUniqueKeys = new Set(recentlyUsedEvents.map((item) => item.value));

    const maxPopularity = events.reduce((previousValue, currentValue) => {
      return previousValue >= (Number(currentValue.uiUniqueQueryCount) || 0)
        ? previousValue
        : Number(currentValue.uiUniqueQueryCount);
    }, 1);

    const eventsWithMeta = events.map((item) => {
      const isRecentlyViewed = recentlyUsedUniqueKeys.has(item.value);
      const usage = isRecentlyViewed ? 2 : 0;
      const popularity = (Number(item.uiUniqueQueryCount) || 0) / maxPopularity;
      const score = usage + popularity;
      item.score = score;
      return {item, usage, popularity, score};
    });

    eventsWithMeta.sort((a, b) => {
      return b.score - a.score;
    });

    return eventsWithMeta.map((entry) => entry.item);
  }
}

/**
 * Prepare one top event entry for display in mp-items-menu
 * @param {object} topEvent - top event from query response
 * @return {object} - object compatible with mp-items-menu component
 */
export function getEventMenuItem(
  topEvent,
  eventDefinition?: EventDefinition,
  isNewMenu = false,
): SectionItem<EventItem> {
  const label = renameEvent(topEvent.name || topEvent.label || topEvent.value);
  return {
    label,
    icon: isNewMenu ? null : getIconForEvent(topEvent),
    searchableContent: [...baseSeachableContent(eventDefinition), eventDefinition?.name].join(` `),
    uiUniqueQueryCount: eventDefinition?.uiUniqueQueryCount,
    value: {
      label,
      custom: false,
      id: null,
      value: topEvent.name || topEvent.value,
    },
  };
}

/**
 * Prepare one custom event entry for display in mp-items-menu
 * @param {object} customEvent - custom event from query response
 * @return {object} - object compatible with mp-items-menu component
 */
export function getCustomEventMenuItem(
  customEvent,
  eventDefinition?: EventDefinition,
  isNewMenu = false,
): SectionItem<EventItem> {
  return {
    icon: isNewMenu ? null : `custom`,
    label: customEvent.name,
    /** @todo [pablo] `uiUniqueQueryCount` is not an item relevant to mp-items-menu */
    uiUniqueQueryCount: eventDefinition?.uiUniqueQueryCount,
    searchableContent: baseSeachableContent(eventDefinition).join(` `),
    value: {
      isCollectEverything: customEvent.is_collect_everything_event,
      custom: true,
      id: customEvent.id,
      label: customEvent.name,
      value: `$custom_event:${customEvent.id}`,
    },
  };
}

function getDataDefinitionsEventMenuItem(eventDefinition: EventDefinition, isNewMenu = false): SectionItem<EventItem> {
  const isCustomEvent = Boolean(eventDefinition.customEventId);
  const eventItem: EventItem = {
    label: isCustomEvent ? eventDefinition.name : renameEvent(eventDefinition.name),
    value: isCustomEvent ? `$custom_event:${eventDefinition.customEventId}` : eventDefinition.name,
    ...(isCustomEvent ? {id: eventDefinition.customEventId, custom: true} : {}),
  };
  return {
    icon: isNewMenu ? null : getIconForEvent(eventItem),
    label: eventItem.label,
    searchableContent: [...baseSeachableContent(eventDefinition), eventDefinition?.name].join(` `),
    /** @todo [pablo] `uiUniqueQueryCount` is not an item relevant to mp-items-menu */
    uiUniqueQueryCount: eventDefinition?.uiUniqueQueryCount,
    value: eventItem,
  };
}

function baseSeachableContent(eventDefinition: EventDefinition): string[] {
  return [eventDefinition?.description, ...(eventDefinition?.tags || []), ...(eventDefinition?.platforms || [])].filter(
    Boolean,
  );
}
